import { Grid, InputLabel } from "@material-ui/core";
import {
  AsyncSelect,
  Button,
  Checkbox,
  FlashMessage,
  Input,
  Modal,
  RichTextEditor,
} from "../../common";
import React, { useEffect, useState } from "react";
import EducationTitleIcon from "../icons/education-title-icon.svg";
import { useFieldArray, useForm } from "react-hook-form";
import DateIcon from "../icons/date-icon.svg";
import PinkPlus from "../icons/plus-pink.svg";
import DeleteIcon from "../icons/delete-icon.svg";
import DegreeIcon from "../icons/degree-icon.svg";
import { useDispatch, useSelector } from "react-redux";
import EditIcon from "../icons/edit-icon.svg";
import _ from "lodash";
import { updateCandidateEducationRequest } from "../../../reducers/candidate/candidate.reducer";
import { mergeSessionUserPartial } from "../../../reducers/auth/session.reducer";
import { rootReducersState } from "../../../reducers";
import commonServices from "../../../services/common.services";
import ReadMoreLess from "../../common/ReadMoreLess";
import EducationIconAfter from "../icons/education-icon-after.svg";
import LocationIcon from "../icons/location-select.svg";
import { formatDate, stripHTMLTags } from "../../../utils/helper";

interface LocationOption {
  value: number | string;
  label: string;
}

interface EducationItem {
  institute_name: string;
  field_of_study: string;
  degree_title: string;
  degree_from: string;
  degree_to: string | null;
  is_present: number | boolean; // 0 or 1
  description: string | null;
  city_id?: number | string | null;
  location?: LocationOption | string | null;
  location_json?: string | { value?: number | string; id?: number | string; label?: string; location?: string } | null;
}

let locationSearchDelay: ReturnType<typeof setTimeout>;
let defaultLocationsRequest: Promise<LocationOption[]> | null = null;

const mapLocationOptions = (locationData: unknown): LocationOption[] =>
  _.get(locationData, "data.data", []).map((city: { id: number; location: string }) => ({
    value: city.id,
    label: city.location,
  }));

const toLocationOption = (edu: Partial<EducationItem> | null | undefined): LocationOption | null => {
  const raw = edu?.location;
  if (_.isPlainObject(raw)) {
    const value = _.get(raw, "value", _.get(raw, "id", null));
    const label = String(_.get(raw, "label", "") || _.get(raw, "location", "")).trim();
    if (value !== null && value !== undefined && value !== "" && label) {
      return { value, label };
    }
  }

  if (typeof raw === "string" && raw.trim() && edu?.city_id) {
    return { value: edu.city_id, label: raw.trim() };
  }

  if (!edu?.location_json) return null;

  try {
    const parsed =
      typeof edu.location_json === "string"
        ? JSON.parse(edu.location_json)
        : edu.location_json;
    if (_.isPlainObject(parsed)) {
      const value = _.get(parsed, "value", _.get(parsed, "id", edu?.city_id ?? null));
      const label = String(_.get(parsed, "label", "") || _.get(parsed, "location", "")).trim();
      if (value !== null && value !== undefined && value !== "" && label) {
        return { value, label };
      }
    }
    if (typeof parsed === "string" && parsed.trim() && edu?.city_id) {
      return { value: edu.city_id, label: parsed.trim() };
    }
  } catch {
    return null;
  }

  return null;
};

const getEducationLocationLabel = (edu: Partial<EducationItem>) => {
  const option = toLocationOption(edu);
  if (option) return option.label;
  return typeof edu?.location === "string" ? edu.location.trim() : "";
};

const normalizeEducationRow = (edu: EducationItem): EducationItem => ({
  ...edu,
  location: toLocationOption(edu),
});

const toEducationApiRow = (edu: EducationItem) => {
  const option = toLocationOption(edu);
  const cityId = option ? Number(option.value) : NaN;
  const hasCityId = !isNaN(cityId) && cityId % 1 === 0;

  return {
    ...edu,
    city_id: hasCityId ? cityId : null,
    location: option?.label || null,
  };
};

const emptyEducation = (): EducationItem => ({
  institute_name: "",
  field_of_study: "",
  degree_title: "",
  degree_from: "",
  degree_to: "",
  is_present: false,
  description: "",
  city_id: null,
  location: null,
});

const searchLocations = (search: string) => {
  const query = String(search || "").trim();

  // Opening the dropdown asks for an empty search. Reuse that list so each
  // education row does not call the locations API again.
  if (!query) {
    if (!defaultLocationsRequest) {
      defaultLocationsRequest = commonServices
        .fetchLocations("")
        .then((locationData) => mapLocationOptions(locationData))
        .catch(() => {
          defaultLocationsRequest = null;
          return [];
        });
    }
    return defaultLocationsRequest;
  }

  return new Promise<LocationOption[]>((resolve) => {
    clearTimeout(locationSearchDelay);
    locationSearchDelay = setTimeout(async () => {
      const locationData = await commonServices.fetchLocations(query);
      resolve(mapLocationOptions(locationData));
    }, 1000);
  });
};

interface EducationSectionProps {
  education: EducationItem[];
  isPublicPage?: boolean;
  openRequest?: boolean;
  onOpenHandled?: () => void;
}
function EducationSection({ education, isPublicPage, openRequest, onOpenHandled }: EducationSectionProps) {
  const [educationModal, setEducationModal] = useState(false);
  const [localData, setLocalData] = useState<EducationItem[]>(education);

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<{ education: EducationItem[] }>({
    defaultValues: {
      education: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "education",
  });

  const candidateViewFlag = useSelector(
    ({ candidate }: rootReducersState) => candidate.candidates.candidateViewFlag
  );

  const openEducationModal = () => {
    if (isPublicPage) return;
    const updatedProjects = watch("education");
    reset({ education: updatedProjects });
    setEducationModal(true);
  };

  useEffect(() => {
    if (!openRequest || isPublicPage) return;
    openEducationModal();
    onOpenHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openRequest, isPublicPage]);

  const closeEducationModal = () => {
    const updatedProjects = watch("education");
    reset({ education: updatedProjects });
    setEducationModal(false);
  };

  useEffect(() => {
    const normalized = (education || []).map(normalizeEducationRow);
    reset({ education: normalized });
    setLocalData(normalized);
  }, [education, reset]);

  const onSubmit = (data: { education: EducationItem[] }) => {
    const payload = {
      education: data.education.map(toEducationApiRow),
    };
    dispatch(updateCandidateEducationRequest(payload));
  };

  useEffect(() => {
    if (candidateViewFlag === "education_success") {
      FlashMessage("Education updated successfully");
      const updatedData = (watch("education") || []).map(normalizeEducationRow);
      setLocalData(updatedData);
      reset({ education: updatedData });
      setEducationModal(false);
      if (!isPublicPage) {
        dispatch(
          mergeSessionUserPartial({
            education: updatedData.map(toEducationApiRow),
          })
        );
      }
    }
    if (candidateViewFlag === "education_failed") {
      FlashMessage("Failed to update education", "error");
    }

    // eslint-disable-next-line
  }, [candidateViewFlag, dispatch, isPublicPage, reset, watch]);

  const _handlePresentCheckBoxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { checked } = e.target;

    setValue(`education.${index}.is_present`, checked ? 1 : 0);

    if (checked) {
      // Hide degree_to if present is true
      setValue(`education.${index}.degree_to`, null);
    } else {
      // Optionally set a default or leave it blank
      setValue(`education.${index}.degree_to`, "");
    }
  };

  return (
    <div className="accordion" id="profile-education">
      <div className="accordion-head">
        <b>Education</b>
        {!isPublicPage &&
          (!_.isEmpty(localData) ? (
            <div className="edit-icon" onClick={openEducationModal}>
              <img src={EditIcon} alt="edit" />
            </div>
          ) : (
            <span onClick={openEducationModal}>+Add</span>
          ))}
      </div>
      {!_.isEmpty(localData) && (
        <div className="accordion-body">
          <div
            className={`education-content${isPublicPage ? " main-public-page profile-timeline-rail" : ""
              }`}
          >
            {localData?.map((edu, index) => (
              <div
                key={index}
                className={`education-item${isPublicPage ? " profile-timeline-item" : ""}`}
              >
                {isPublicPage && (
                  <span
                    className="profile-timeline-icon profile-timeline-icon--education"
                    aria-hidden="true"
                  >
                    <img src={EducationIconAfter} alt="" />
                  </span>
                )}
                <h5>{edu.institute_name}</h5>
                <span>{edu.degree_title}</span>
                {getEducationLocationLabel(edu) ? (
                  <span className="education-location">{getEducationLocationLabel(edu)}</span>
                ) : null}
                {
                  isPublicPage && (
                    <div className="education-item-content">
                      <div className="date-badge">
                        <span className="start-date">{formatDate(edu.degree_from)}</span> -
                        <span className="end-date">{edu.degree_to ? formatDate(edu.degree_to) : "Present"}</span>
                      </div>
                      {
                        edu?.description && (
                          <div className="bordered-content wysiwyg-data">
                            <ReadMoreLess text={edu.description || ""} textLimit={300} />
                          </div>
                        )
                      }
                    </div>
                  )
                }
              </div>
            ))}
          </div>
        </div>
      )}
      {!isPublicPage && (
        <Modal
          visible={educationModal}
          onClose={closeEducationModal}
          className="candidate-profile-modal project-modal"
          title={!_.isEmpty(localData) ? "Edit Education" : "Education"}
        >
          <div className="modal-body-wrapper">
            <form onSubmit={handleSubmit(onSubmit)}>
              {!_.isEmpty(fields) ? (
                fields.map((item, index) => (
                  <div className="form-wrapper" key={item.id}>
                    <div className="form-group">
                      <Input
                        inputIcon={
                          <div className="input-icon">
                            <img src={EducationTitleIcon} alt="icon" />
                          </div>
                        }
                        className="input-with-icon"
                        externalLabel={{ label: "University/Institute", className: "required" }}
                        placeholder="University/Institute"
                        validationObj={errors}
                        defaultValue={item.institute_name}
                        {...register(`education.${index}.institute_name`, {
                          required: {
                            value: true,
                            message: "Please enter the University/Institute.",
                          },
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <AsyncSelect
                        control={control}
                        validationObj={errors}
                        placeholder="Select location"
                        externalLabel={{ label: "Location" }}
                        name={`education.${index}.location`}
                        icon={<img src={LocationIcon} alt="" />}
                        onStartSearching={searchLocations}
                        noOptionsMessage="No matching locations found"
                      />
                    </div>
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <div className="form-group">
                          <Input
                            inputIcon={
                              <div className="input-icon">
                                <img src={DegreeIcon} alt="icon" />
                              </div>
                            }
                            className="input-with-icon"
                            externalLabel={{ label: "Degree", className: "required" }}
                            placeholder="Degree"
                            validationObj={errors}
                            defaultValue={item.degree_title}
                            {...register(`education.${index}.degree_title`, {
                              required: {
                                value: true,
                                message: "Please enter the Degree.",
                              },
                            })}
                          />
                        </div>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <div className="form-group">
                          <Input
                            inputIcon={
                              <div className="input-icon">
                                <img src={DegreeIcon} alt="icon" />
                              </div>
                            }
                            className="input-with-icon"
                            externalLabel={{ label: "Field Of Study", className: "required" }}
                            placeholder="Field of Study"
                            validationObj={errors}
                            defaultValue={item.field_of_study}
                            {...register(`education.${index}.field_of_study`, {
                              required: {
                                value: true,
                                message: "Please enter the Field of Study.",
                              },
                            })}
                          />
                        </div>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid item sm={6} xs={12}>
                        <div className="form-group">
                          <Input
                            inputIcon={
                              <div className="input-icon">
                                <img src={DateIcon} alt="icon" />
                              </div>
                            }
                            type="date"
                            className="input-with-icon"
                            externalLabel={{ label: "Start Date", className: "required" }}
                            placeholder="Start Date"
                            validationObj={errors}
                            defaultValue={item.degree_from}
                            inputProps={{
                              max: new Date().toISOString().split('T')[0]
                            }}
                            {...register(`education.${index}.degree_from`, {
                              required: {
                                value: true,
                                message: "Please enter the Start Date.",
                              },
                              validate: (value) => {
                                if (!value) return true;
                                const today = new Date().toISOString().split('T')[0];
                                if (value > today) {
                                  return "Start date cannot be in the future";
                                }
                                return true;
                              },
                            })}
                          />
                        </div>
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <div className="form-group">
                          {watch(`education.${index}.is_present`) ? (
                            <InputLabel className="present-label">Present</InputLabel>
                          ) : (
                            <Input
                              inputIcon={
                                <div className="input-icon">
                                  <img src={DateIcon} alt="icon" />
                                </div>
                              }
                              type="date"
                              className="input-with-icon"
                              externalLabel={{ label: "End Date" }}
                              placeholder="End Date"
                              validationObj={errors}
                              defaultValue={item.degree_to}
                              inputProps={{
                                min: getValues(`education.${index}.degree_from`) || item.degree_from || "",
                                max: new Date().toISOString().split('T')[0]
                              }}
                              {...register(`education.${index}.degree_to`, {
                                required: {
                                  value: false,
                                  message: "Please enter the End Date.",
                                },
                                validate: (value) => {
                                  if (!value) return true; // Allow empty if not required
                                  const today = new Date().toISOString().split('T')[0];
                                  if (value > today) {
                                    return "End date cannot be in the future";
                                  }
                                  const startDate = getValues(`education.${index}.degree_from`) || item.degree_from;
                                  if (startDate && value < startDate) {
                                    return "End date cannot be less than start date";
                                  }
                                  return true;
                                },
                              })}
                              disabled={watch(`education.${index}.is_present`)}
                            />
                          )}
                        </div>
                      </Grid>
                    </Grid>

                    <div className="from-group">
                      <Checkbox
                        {...register(`education.${index}.is_present`, {
                          required: {
                            value: false,
                            message: "Please enter the checkbox.",
                          },
                        })}
                        defaultChecked={
                          item?.is_present === 1 || item?.is_present === true
                        }
                        externalLabel={{ label: "Present" }}
                        onChange={(e: any) =>
                          _handlePresentCheckBoxChange(e, index)
                        }
                      />
                    </div>

                    <div className="form-group ">
                      <RichTextEditor
                        key={item.id}
                        name={`education.${index}.description`}
                        externalLabel={{
                          label: "Description",
                        }}
                        placeholder="Start writing..."
                        defaultValue={item?.description || ""} // Default to empty string if undefined
                        validationObj={errors}
                        control={control}
                        rules={
                          {
                            validate: {
                              validLength: (value: any) => {
                                const tmpText = stripHTMLTags(value || "");
                                if (tmpText.length > 1000) {
                                  return "Can not exceed more then 1000 characters";
                                }
                                return true;
                              },
                            },
                            required: {
                              value: false,
                              message: "Please enter education description",
                            },
                          }}
                        showCount
                        maxLimit={1000}
                        counterAlign="right"
                      />
                      <div className="action-wrapper">
                        <div
                          className="icon"
                          onClick={() => append(emptyEducation())}
                        >
                          <img src={PinkPlus} alt="edit" />
                        </div>
                        <div className="icon">
                          <img
                            src={DeleteIcon}
                            alt="Delete"
                            onClick={() => remove(index)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <Button
                  type="button"
                  className="add-new-btn"
                  onClick={() => append(emptyEducation())}
                >
                  Add new
                </Button>
              )}
              <div className="btn-wrapper">
                <Button
                  variant="outlined"
                  className="outlined"
                  onClick={() => closeEducationModal()}
                  color="none"
                >
                  Cancel
                </Button>
                <Button className="save-btn" type="submit">
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default EducationSection;
